import { API_SERVICE } from "@/services/api.service";
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

export async function downloadDocument(fileID, clientName){
    try {
        const res = await API_SERVICE.get("peoplePortalRestAPI", `/download/${fileID}?clientName=${clientName}`);
        if(res.status === "200"){
            if (Capacitor.isNativePlatform()) {
                await downloadNative(res.data);
            } else {
                doDownload(res.data);
            }
        }
    } catch (error) {
        console.error('Download error:', error);
    }
}

async function downloadNative(fileObject) {
    try {
        console.log('[Download] Starting native download for:', fileObject.FileName);
        
        const fileName = fileObject.FileName || `download_${new Date().getTime()}`;
        const mimeType = fileObject.ContentType || 'application/octet-stream';
        
        // Method 1: Try to save using Filesystem plugin with proper permissions
        try {
            // Check if we can get permissions for Downloads folder
            const permissions = await Filesystem.checkPermissions();
            console.log('[Download] Filesystem permissions:', permissions);
            
            // Try multiple directory options
            const directoryOptions = [
                { path: `Download/${fileName}`, directory: Directory.ExternalStorage },
                { path: fileName, directory: Directory.Documents },
                { path: fileName, directory: Directory.Data }
            ];
            
            let savedSuccessfully = false;
            let result;
            
            for (const option of directoryOptions) {
                try {
                    result = await Filesystem.writeFile({
                        path: option.path,
                        data: fileObject.Content,
                        directory: option.directory,
                        recursive: true
                    });
                    console.log(`[Download] File saved successfully to ${option.directory}:`, result.uri);
                    savedSuccessfully = true;
                    break;
                } catch (dirError) {
                    console.log(`[Download] Failed to save to ${option.directory}:`, dirError.message);
                }
            }
            
            if (savedSuccessfully && result) {
                // Create a data URL and trigger download through browser
                const blob = base64ToBlob(fileObject.Content, mimeType);
                const url = URL.createObjectURL(blob);
                
                // Create and click download link
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up
                setTimeout(() => URL.revokeObjectURL(url), 100);
                
                console.log('[Download] File download triggered successfully');
                return result;
            }
        } catch (fsError) {
            console.error('[Download] Filesystem method failed:', fsError);
        }
        
        // Method 2: Fall back to web download
        console.log('[Download] Falling back to web download method');
        doDownloadWeb(fileObject);
        
        return { success: true };
        
    } catch (error) {
        console.error('[Download] Native download error:', error);
        throw error;
    }
}

// Helper function to convert base64 to blob
function base64ToBlob(base64Data, contentType) {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
}

export async function doDownload(fileObject) {
    console.log('[Download] doDownload called with:', {
        fileName: fileObject.FileName,
        contentType: fileObject.ContentType,
        contentLength: fileObject.Content ? fileObject.Content.length : 0,
        isNativePlatform: Capacitor.isNativePlatform()
    });
    
    // Check if we're on a native platform
    if (Capacitor.isNativePlatform()) {
        try {
            await downloadNative(fileObject);
        } catch (error) {
            console.error('[Download] Native download failed, falling back to web download:', error);
            doDownloadWeb(fileObject);
        }
    } else {
        doDownloadWeb(fileObject);
    }
}

function doDownloadWeb(fileObject) {
    // Decode the base64 content
    const binaryContent = atob(fileObject.Content);
    
    // Create a Uint8Array from the binary content
    const uint8Array = new Uint8Array(binaryContent.length);
    for (let i = 0; i < binaryContent.length; i++) {
      uint8Array[i] = binaryContent.charCodeAt(i);
    }
    
    // Create a Blob from the Uint8Array
    const blob = new Blob([uint8Array], { type: fileObject.ContentType });
    
    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    link.download = fileObject.FileName;
    
    // Append the link to the body, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Revoke the Blob URL to free up memory
    URL.revokeObjectURL(url);
  }